import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Page } from '../../components/Layout';
import { ErrorState, LoadingState } from '../../components/states';
import { Button, Card, SectionTitle } from '../../components/ui';
import { useMyStats } from '../../hooks/queries';
import { useRuntime } from '../../hooks/useRuntime';
import { api } from '../../lib/api';
import { useTracking } from '../../services/runtime';
import { useAuth } from '../../stores/auth';
import { toast } from '../../stores/toasts';
import { PlayerStatsView } from './PlayerStatsView';
import { RunHistory } from './RunHistory';
import { setAppBadge } from '../../lib/app-badge';
import { detachPushOnLogout } from '../../lib/push';
import { NotificationsCard } from './NotificationsCard';
import { LanguageCard, SettingsCard } from './SettingsCard';
import { LegalLinks } from '../legal/LegalPages';
import { RivalsCard } from '../rivals/RivalsPages';
import { ConnectionsCard } from '../imports/ConnectionsCard';
import { useT } from '../../i18n';
import { DeleteAccountSheet } from './DeleteAccountSheet';

export function ProfilePage() {
  const stats = useMyStats();
  const clear = useAuth((s) => s.clear);
  const email = useAuth((s) => (s.user?.hasEmail === false ? null : s.user?.email));
  const role = useAuth((s) => s.user?.role);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { runtime } = useRuntime();
  const runState = useTracking((s) => s.state);
  const t = useT();

  const [deleting, setDeleting] = useState(false);
  const running = runState === 'RUNNING' || runState === 'PAUSED' || runState === 'RESUMING';

  function leave() {
    runtime?.realtime.disconnect();
    clear();
    qc.clear();
    navigate('/login', { replace: true });
  }

  async function logout() {
    if (running) {
      toast({ kind: 'error', title: t('Finish your run first') });
      return;
    }
    await detachPushOnLogout();
    setAppBadge(0);
    try {
      await api('/auth/logout', { method: 'POST', timeoutMs: 5000 });
    } catch {
      // Logging out locally still works offline; the session expires server-side.
    }
    leave();
  }

  function accountDeleted() {
    // Push subscriptions and sessions went with the account on the server.
    setAppBadge(0);
    toast({ kind: 'success', title: t('Your account was deleted') });
    leave();
  }

  return (
    <Page>
      {stats.isPending ? (
        <LoadingState />
      ) : stats.isError && !stats.data ? (
        <ErrorState error={stats.error} onRetry={() => stats.refetch()} />
      ) : stats.data ? (
        <PlayerStatsView stats={stats.data} isMe />
      ) : null}
      <Button variant="secondary" className="mt-4 w-full" onClick={() => navigate('/profile/edit')} data-testid="edit-profile">
        {t('Edit profile')}
      </Button>
      <Link to="/offers" className="mt-3 block" data-testid="profile-offers">
        <Card className="flex items-center gap-3">
          <span className="text-2xl">🎁</span>
          <span className="flex-1 font-semibold">{t('My offers')}</span>
          <span className="text-ink-500">›</span>
        </Card>
      </Link>
      <SectionTitle>{t('Rivals')}</SectionTitle>
      <RivalsCard />
      <SectionTitle>{t('Run history')}</SectionTitle>
      <RunHistory />
      <SectionTitle>{t('Connections')}</SectionTitle>
      <ConnectionsCard />
      <SectionTitle>{t('Notifications')}</SectionTitle>
      <NotificationsCard />
      <SectionTitle>{t('Language')}</SectionTitle>
      <LanguageCard />
      <SectionTitle>{t('Privacy')}</SectionTitle>
      <SettingsCard />
      {role === 'SUPER_ADMIN' ? (
        <Button variant="secondary" className="mt-6 w-full" onClick={() => navigate('/admin')} data-testid="open-admin">
          Game owner · Stats &amp; moderation
        </Button>
      ) : null}
      <Link to="/guide" className="mt-6 block" data-testid="profile-guide">
        <Card className="flex items-center gap-3">
          <span className="text-2xl">📖</span>
          <span className="flex-1 font-semibold">{t('Guide & help')}</span>
          <span className="text-ink-500">›</span>
        </Card>
      </Link>
      <LegalLinks className="mt-4" />
      <div className="mt-6 text-center text-xs text-ink-500">{email}</div>
      <Button variant="ghost" className="mt-2 w-full" onClick={logout} data-testid="logout">
        {t('Sign out')}
      </Button>
      {role !== 'SUPER_ADMIN' ? (
        <button
          className="mt-6 w-full py-2 text-center text-xs font-semibold text-enemy/80"
          onClick={() => (running ? toast({ kind: 'error', title: t('Finish your run first') }) : setDeleting(true))}
          data-testid="delete-account"
        >
          {t('Delete account')}
        </button>
      ) : null}
      <DeleteAccountSheet open={deleting} onClose={() => setDeleting(false)} onDeleted={accountDeleted} />
    </Page>
  );
}
